import { useState } from 'react';
import { Link } from 'react-router-dom';
import { getVehicle } from 'features/vehicles/services/vehicleApi';
import { reviewVehicleVerification } from 'features/admin/services/adminApi';
import { ROUTES } from 'core/router/routes';

export default function AdminVehicleReview() {
  const [vehicleId, setVehicleId] = useState('');
  const [vehicle, setVehicle] = useState<Awaited<ReturnType<typeof getVehicle>> | null>(null);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = async () => {
    if (!vehicleId.trim()) return;
    setLoading(true);
    setMessage(null);
    try {
      setVehicle(await getVehicle(vehicleId.trim()));
    } catch {
      setMessage('Vehicle not found.');
      setVehicle(null);
    } finally {
      setLoading(false);
    }
  };

  const review = async (verificationStatus: 'verified' | 'rejected') => {
    if (!vehicle) return;
    setLoading(true);
    try {
      await reviewVehicleVerification(vehicle.id, {
        verificationStatus,
        rejectionReason: verificationStatus === 'rejected' ? reason : undefined,
      });
      setMessage(`Marked as ${verificationStatus}.`);
      await load();
    } catch {
      setMessage('Review failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto p-8 font-display">
      <Link to={ROUTES.ADMIN.USERS} className="text-sm text-primary font-bold">
        ← Users
      </Link>
      <h1 className="text-2xl font-black mt-4 mb-6">Vehicle verification</h1>
      <div className="flex gap-2 mb-6">
        <input
          value={vehicleId}
          onChange={(e) => setVehicleId(e.target.value)}
          placeholder="Vehicle UUID"
          className="flex-1 rounded-xl border px-3 py-2 text-sm"
        />
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          className="bg-primary text-white font-bold px-4 py-2 rounded-xl text-sm"
        >
          Load
        </button>
      </div>
      {message && <p className="text-sm mb-4">{message}</p>}
      {vehicle && (
        <div className="bg-white border rounded-2xl p-6 space-y-3">
          <p className="font-bold">{vehicle.plateNumber ?? vehicle.id}</p>
          <p className="text-sm">Status: {vehicle.verificationStatus}</p>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Rejection reason"
            className="w-full rounded-xl border px-3 py-2 text-sm"
            rows={2}
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void review('verified')}
              className="bg-emerald-600 text-white font-bold px-4 py-2 rounded-xl text-sm"
            >
              Approve
            </button>
            <button
              type="button"
              onClick={() => void review('rejected')}
              className="bg-red-600 text-white font-bold px-4 py-2 rounded-xl text-sm"
            >
              Reject
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
